// The scripted red cell: an explainable behavior tree per posture (dials.red). See spec §4 "Red cell"
// and plan Task 11. Red obeys the same rules as blue - its orders go through the same OrderBook (net
// serialization + C2 delay) - and it sees blue only through its own Picture (side 1). It never reads
// blue's axes or NAIs; the only graphics it reasons about are the phase lines and objective baked into
// the Scenario it was built from (terrain-derived, not player-drawn). No Math.random: red makes no rolls
// of its own (the `rng` parameter exists only for interface parity with the other per-tick step
// functions, e.g. stepDetection/stepCombat, which do roll).
import type { Contact, Dials, Graphic, Picture, Scenario, Unit } from './types';
import { TICKS_PER_MIN } from './types';
import type { Grid } from './grid';
import type { Rng } from './rng';
import type { EventLog } from './events';
import type { OrderBook } from './orders';
import { UNIT_DEFS } from './scenario';
import { loadRules, type Rules } from './rules';

const DEFAULT_RULES = loadRules('realistic');

type Axis = 'x' | 'y';
type Third = 0 | 1 | 2;

const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['done', 'cancelled', 'rejected']);
/** Contacts within this many meters of each other cluster together for a fire mission. */
const CLUSTER_RADIUS_M = 600;
/** Reserve commitment trigger: total detected blue platoons past PL WHITE (or PL RED, for 'forward') in one third. */
const RESERVE_TRIGGER_PLATOONS = 2;
/** Main-body strength threshold below which the reserve reinforces. */
const REINFORCE_STRENGTH_THRESHOLD = 50;
/** Screen/delay withdrawal threshold: engaged units below this strength fall back one PL. */
const DELAY_STRENGTH_THRESHOLD = 70;
/** ISR/recon-screen/resupply re-evaluate at most once per this many ticks per unit. */
const ISR_COOLDOWN_TICKS = 10 * TICKS_PER_MIN;
const SCREEN_MOVE_COOLDOWN_TICKS = 10 * TICKS_PER_MIN;
const RESUPPLY_INTERVAL_TICKS = 20 * TICKS_PER_MIN;
/** Types the 'attack' card allows and that make up red's combat power for a forward counterattack. */
const COUNTERATTACK_TYPES: ReadonlySet<string> = new Set(['armor', 'mech', 'inf', 'recon', 'spArty', 'mortar']);

interface Waypoint { name: string; depthVal: number; kind: 'pl' | 'obj'; graphic: Graphic }

function centroidOf(contacts: Contact[]): { x: number; y: number } {
  let sx = 0, sy = 0;
  for (const c of contacts) { sx += c.x; sy += c.y; }
  return { x: Math.round(sx / contacts.length), y: Math.round(sy / contacts.length) };
}

/** Connected components of contacts within CLUSTER_RADIUS_M of each other (union-find). */
function clusterContacts(contacts: Contact[], grid: Grid): Contact[][] {
  const n = contacts.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  const union = (a: number, b: number): void => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = grid.distCm({ x: contacts[i].x, y: contacts[i].y }, { x: contacts[j].x, y: contacts[j].y }) / 100;
      if (d <= CLUSTER_RADIUS_M) union(i, j);
    }
  }
  const groups = new Map<number, Contact[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r)!.push(contacts[i]);
  }
  return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
}

function thirdOfRange(w: number, widthMin: number, widthMax: number): Third {
  const span = Math.max(1, widthMax - widthMin);
  const frac = Math.min(1, Math.max(0, (w - widthMin) / span));
  return Math.min(2, Math.floor(frac * 3)) as Third;
}

/** Cells the grid crosses along a graphic's polyline (walked in cell-sized steps), deduplicated. */
function cellsAlongPl(pl: Graphic, grid: Grid): number[] {
  const cells = new Set<number>();
  for (let i = 0; i < pl.pts.length - 1; i++) {
    const p1 = pl.pts[i], p2 = pl.pts[i + 1];
    const dist = grid.distCm(p1, p2);
    const steps = Math.max(1, Math.round(dist / grid.cellCm));
    for (let s = 0; s <= steps; s++) {
      const f = s / steps;
      const x = Math.round(p1.x + (p2.x - p1.x) * f);
      const y = Math.round(p1.y + (p2.y - p1.y) * f);
      cells.add(grid.cellOfCm(x, y));
    }
  }
  if (pl.pts.length === 1) cells.add(grid.cellOfCm(pl.pts[0].x, pl.pts[0].y));
  return [...cells];
}

/** Passable neighbors (8-connected) of a cell, for picking chokepoints. */
function passableNeighborCount(grid: Grid, cell: number): number {
  const cx = grid.cx(cell), cy = grid.cy(cell);
  let n = 0;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue;
      const nx = cx + dx, ny = cy + dy;
      if (!grid.inBounds(nx, ny)) continue;
      if (grid.passable(grid.index(nx, ny))) n++;
    }
  }
  return n;
}

function widthOfCell(grid: Grid, cell: number, widthAxis: Axis): number {
  const c = grid.centerCm(cell);
  return widthAxis === 'x' ? c.x : c.y;
}

/** The passable cell on `pl`, within `third`, with the most passable neighbors (ties: lowest cell id). */
function chokepointCell(grid: Grid, pl: Graphic, third: Third, widthAxis: Axis, widthMin: number, widthMax: number): number | null {
  const candidates = cellsAlongPl(pl, grid).filter(
    (c) => grid.passable(c) && thirdOfRange(widthOfCell(grid, c, widthAxis), widthMin, widthMax) === third,
  );
  if (candidates.length === 0) return null;
  let best = candidates[0];
  let bestScore = passableNeighborCount(grid, best);
  for (let i = 1; i < candidates.length; i++) {
    const score = passableNeighborCount(grid, candidates[i]);
    if (score > bestScore || (score === bestScore && candidates[i] < best)) { best = candidates[i]; bestScore = score; }
  }
  return best;
}

/** The nearest passable cell on `pl`, within `third`, to `from`. Falls back to any third if that third has none on this PL. */
function nearestCellOnPl(
  grid: Grid, pl: Graphic, third: Third, widthAxis: Axis, widthMin: number, widthMax: number, from: { x: number; y: number },
): number | null {
  const all = cellsAlongPl(pl, grid).filter((c) => grid.passable(c));
  const inThird = all.filter((c) => thirdOfRange(widthOfCell(grid, c, widthAxis), widthMin, widthMax) === third);
  const pool = inThird.length > 0 ? inThird : all;
  if (pool.length === 0) return null;
  let best = pool[0];
  let bestD = grid.distCm(from, grid.centerCm(best));
  for (let i = 1; i < pool.length; i++) {
    const d = grid.distCm(from, grid.centerCm(pool[i]));
    if (d < bestD || (d === bestD && pool[i] < best)) { best = pool[i]; bestD = d; }
  }
  return best;
}

/**
 * Scripted red behavior tree. One instance per Sim, built once from the Scenario (whose phase lines and
 * objective are terrain-derived, not the player's drawn graphics). `step` runs the tree once per sim
 * minute (t % 10 === 0) per spec §4.
 */
export class RedCell {
  private readonly depthAxis: Axis;
  private readonly widthAxis: Axis;
  private readonly widthMin: number;
  private readonly widthMax: number;
  private readonly widthNames: readonly [string, string, string];
  private readonly depthSign: 1 | -1;
  private readonly plRedDepth: number;
  private readonly plWhiteDepth: number;
  private readonly plRed: Graphic | null;
  private readonly plWhite: Graphic | null;
  private readonly plBlue: Graphic | null;
  private readonly obj: Graphic | null;
  private readonly waypoints: Waypoint[];

  private initialIssued = false;
  /** Reserve (armor) unit ids ever committed (to a reserve counterattack or a reinforcement) - permanent: a committed reserve unit is never reconsidered, which is how "once" and "do not re-issue while non-terminal" are satisfied here. */
  private readonly committedReserve = new Set<number>();
  /** Main-body unit ids that have already triggered a reinforcement, so a unit hovering under the threshold doesn't ask for a second reserve unit. */
  private readonly reinforcedFor = new Set<number>();
  /** Per-artillery-unit tick at which it may next consider a fire mission (rules.fireMission.cooldownMin). */
  private readonly nextArtyEligible = new Map<number, number>();
  /** Per-unit (uas/recon) tick at which ISR / recon-screen may next re-task it. */
  private readonly nextIsrEligible = new Map<number, number>();
  /** Per-log-unit tick at which it may next be sent on a resupply run. */
  private readonly nextResupplyEligible = new Map<number, number>();
  /** 'forward' only: whether the one-shot "counterattack with everything" has already fired. */
  private forwardCommitted = false;

  constructor(private readonly scenario: Scenario, private readonly dials: Dials, private readonly rules: Rules = DEFAULT_RULES) {
    const pls = scenario.graphics.filter((g) => g.kind === 'pl');
    const plRed = pls.find((g) => g.name.toUpperCase().includes('RED')) ?? pls[0] ?? null;
    const plWhite = pls.find((g) => g.name.toUpperCase().includes('WHITE')) ?? pls[1] ?? plRed;
    const plBlue = pls.find((g) => g.name.toUpperCase().includes('BLUE')) ?? pls[2] ?? null;
    const obj = scenario.graphics.find((g) => g.id === scenario.objectiveId) ?? scenario.graphics.find((g) => g.kind === 'obj') ?? null;

    const refLine = plWhite ?? plRed ?? plBlue;
    if (refLine && refLine.pts.length >= 2) {
      const [p1, p2] = refLine.pts;
      const dx = Math.abs(p2.x - p1.x), dy = Math.abs(p2.y - p1.y);
      // The PL polyline runs the width of the corridor at a fixed depth: whichever coordinate varies
      // along it is the width axis (thirds are grouped across it); the other is the depth (advance) axis.
      if (dy > dx) { this.depthAxis = 'x'; this.widthAxis = 'y'; } else { this.depthAxis = 'y'; this.widthAxis = 'x'; }
      this.widthMin = Math.min(p1[this.widthAxis], p2[this.widthAxis]);
      this.widthMax = Math.max(p1[this.widthAxis], p2[this.widthAxis]);
    } else {
      this.depthAxis = 'x'; this.widthAxis = 'y'; this.widthMin = 0; this.widthMax = 1;
    }
    // North-south-running lines (width axis y, since row 0 is the south edge per Grid) -> south/center/north;
    // east-west-running lines (width axis x, column 0 is the west edge) -> west/center/east.
    this.widthNames = this.widthAxis === 'y' ? ['south', 'center', 'north'] : ['west', 'center', 'east'];

    this.plWhiteDepth = plWhite ? this.depthOf(plWhite.pts[0]) : 0;
    this.plRedDepth = plRed ? this.depthOf(plRed.pts[0]) : this.plWhiteDepth;
    const objDepth = obj ? this.depthOf(obj.pts[0]) : this.plWhiteDepth + 1;
    this.depthSign = objDepth >= this.plWhiteDepth ? 1 : -1;
    this.plRed = plRed;
    this.plWhite = plWhite;
    this.plBlue = plBlue;
    this.obj = obj;

    this.waypoints = [];
    if (plRed) this.waypoints.push({ name: plRed.name, depthVal: this.depthOf(plRed.pts[0]), kind: 'pl', graphic: plRed });
    if (plWhite) this.waypoints.push({ name: plWhite.name, depthVal: this.depthOf(plWhite.pts[0]), kind: 'pl', graphic: plWhite });
    if (plBlue) this.waypoints.push({ name: plBlue.name, depthVal: this.depthOf(plBlue.pts[0]), kind: 'pl', graphic: plBlue });
    if (obj) this.waypoints.push({ name: obj.name, depthVal: objDepth, kind: 'obj', graphic: obj });
    this.waypoints.sort((a, b) => this.depthSign * (a.depthVal - b.depthVal));
  }

  private depthOf(p: { x: number; y: number }): number { return this.depthAxis === 'x' ? p.x : p.y; }
  private widthOf(p: { x: number; y: number }): number { return this.widthAxis === 'x' ? p.x : p.y; }
  private thirdOf(w: number): Third { return thirdOfRange(w, this.widthMin, this.widthMax); }

  /**
   * Runs the red behavior tree. No-op unless t % 10 === 0 (once per sim minute). `graphics` is blue's
   * graphics list, passed through to OrderBook.issue (which needs it to resolve graphic targets) but
   * never read for red's own decisions - red reasons only about scenario.graphics (its own PLs/objective).
   */
  step(t: number, units: Unit[], picture: Picture, orders: OrderBook, grid: Grid, graphics: Graphic[], log: EventLog, rng: Rng): void {
    void rng; // scripted red cell makes no rolls of its own; kept for interface parity with the other stepX functions
    if (t % TICKS_PER_MIN !== 0) return;

    const red = units.filter((u) => u.side === 1).sort((a, b) => a.id - b.id);

    if (!this.initialIssued) {
      for (const u of red) {
        if (!u.alive) continue;
        const cell = grid.cellOfCm(u.x, u.y);
        orders.issue(t, 1, u.id, 'defend', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:initial');
      }
      this.initialIssued = true;
    }

    this.stepArtillery(t, red, picture, orders, units, grid, graphics, log);
    this.stepIsr(t, red, picture, orders, units, grid, graphics, log);
    this.stepResupply(t, red, orders, units, grid, graphics, log);

    if (this.dials.red === 'depth') {
      this.stepReserve(t, red, picture, orders, units, grid, graphics, log);
      this.stepReinforce(t, red, orders, units, grid, graphics, log);
    } else if (this.dials.red === 'screen') {
      this.stepReconScreen(t, red, orders, units, grid, graphics, log);
      this.stepDelay(t, red, orders, units, grid, graphics, log);
    } else if (this.dials.red === 'forward') {
      // Mass on PL RED is already the initial defend (and the fitter's own placement for this posture);
      // the one thing 'forward' still needs is to stop being purely static once blue actually arrives.
      this.stepForward(t, red, picture, orders, units, grid, graphics, log);
    }
  }

  /** spArty/mortar: every `rules.fireMission.cooldownMin` minutes, if ammo > 30 and there's a fresh
   * contact cluster in range, fire on its centroid. (OrderBook.issue itself re-checks the observer
   * requirement per pitch 9c; a cluster centroid the clustering radius allows can occasionally sit just
   * outside the observer radius of any single contact, in which case the order comes back rejected and
   * red simply does not fire that minute - it is not exempt from the same rule it enforces on blue.) */
  private stepArtillery(t: number, red: Unit[], picture: Picture, orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    const artUnits = red.filter((u) => u.alive && (u.type === 'spArty' || u.type === 'mortar'));
    if (artUnits.length === 0) return;

    const freshTicks = this.rules.fireMission.observerFreshTicks;
    const fresh = [...picture.contacts.values()]
      .filter((c) => c.reportedAt <= t && t - c.lastSeen <= freshTicks)
      .sort((a, b) => a.enemyId - b.enemyId);
    if (fresh.length < 2) return;
    const clusters = clusterContacts(fresh, grid).filter((c) => c.length >= 2);
    if (clusters.length === 0) return;

    for (const u of artUnits) {
      const eligible = t >= (this.nextArtyEligible.get(u.id) ?? 0);
      if (!eligible || u.ammo <= 30) continue;

      const weaponRangeM = UNIT_DEFS[u.type].weaponRangeM;
      let best: Contact[] | null = null;
      let bestDist = Infinity;
      for (const cluster of clusters) {
        const centroid = centroidOf(cluster);
        const dist = grid.distCm({ x: u.x, y: u.y }, centroid) / 100;
        if (dist > weaponRangeM) continue;
        if (!best || cluster.length > best.length || (cluster.length === best.length && dist < bestDist)) {
          best = cluster; bestDist = dist;
        }
      }
      if (!best) continue;

      const centroid = centroidOf(best);
      const cell = grid.cellOfCm(centroid.x, centroid.y);
      const order = orders.issue(t, 1, u.id, 'fireMission', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:fires', picture);
      this.nextArtyEligible.set(u.id, t + this.rules.fireMission.cooldownMin * TICKS_PER_MIN);
      if (order.status !== 'rejected') {
        log.push(t, 'red.decision', 1, {
          reason: `Fire mission: ${u.callsign} engaging a cluster of ${best.length} contacts within range`,
          playId: 'red:fires', unitIds: [u.id],
        }, { significant: false });
      }
    }
  }

  /**
   * ISR (intelligence, surveillance, reconnaissance): every `ISR_COOLDOWN_TICKS`, red's UAS (unmanned
   * aircraft system) team launches (or re-launches, once its airframe is lost or grounded) toward the
   * corridor third with the most detected blue contacts - or the center third if it has none to go on
   * yet. Every posture gets this; it is red actually using its own ISR card, not just its scripted maneuver.
   */
  private stepIsr(t: number, red: Unit[], picture: Picture, orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    const uasUnit = red.find((u) => u.alive && u.type === 'uas');
    if (!uasUnit || uasUnit.uasAlive) return;
    const eligible = t >= (this.nextIsrEligible.get(uasUnit.id) ?? 0);
    if (!eligible) return;
    const inFlight = orders.orders.some((o) => o.unitId === uasUnit.id && o.card === 'launchUas' && !TERMINAL_STATUSES.has(o.status));
    if (inFlight) return;

    const sums: [number, number, number] = [0, 0, 0];
    for (const c of picture.contacts.values()) {
      if (c.reportedAt > t) continue;
      sums[this.thirdOf(this.widthOf({ x: c.x, y: c.y }))] += 1;
    }
    let best: Third = 1; // default to the center third when nothing has been seen yet
    for (let i = 0; i < 3; i++) if (sums[i] > sums[best]) best = i as Third;

    const referenceLine = this.plWhite ?? this.plRed ?? this.plBlue;
    const cell = referenceLine ? chokepointCell(grid, referenceLine, best, this.widthAxis, this.widthMin, this.widthMax) : null;
    if (cell === null) return;

    const order = orders.issue(t, 1, uasUnit.id, 'launchUas', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:isr');
    this.nextIsrEligible.set(uasUnit.id, t + ISR_COOLDOWN_TICKS);
    if (order.status !== 'rejected') {
      const contactsNote = sums[best] > 0 ? ` (${sums[best]} contacts detected there)` : ' (nothing detected yet - orbiting the center)';
      log.push(t, 'red.decision', 1, {
        reason: `ISR: ${uasUnit.callsign} launching to orbit the ${this.widthNames[best]} third${contactsNote}`,
        playId: 'red:isr', unitIds: [uasUnit.id],
      }, { significant: false });
    }
  }

  /** screen only: recon on PL RED actively screens - re-issuing its recon-the-line card periodically -
   * instead of sitting idle after the opening defend. */
  private stepReconScreen(t: number, red: Unit[], orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    if (!this.plRed) return;
    const reconUnits = red.filter((u) => u.alive && u.type === 'recon');
    for (const u of reconUnits) {
      const eligible = t >= (this.nextIsrEligible.get(u.id) ?? 0);
      if (!eligible) continue;
      const inFlight = orders.orders.some((o) => o.unitId === u.id && o.playId === 'red:screen' && !TERMINAL_STATUSES.has(o.status));
      if (inFlight) continue;

      const order = orders.issue(t, 1, u.id, 'recon', { kind: 'graphic', graphicId: this.plRed.id }, units, grid, graphics, this.dials, 'red:screen');
      this.nextIsrEligible.set(u.id, t + SCREEN_MOVE_COOLDOWN_TICKS);
      if (order.status !== 'rejected') {
        log.push(t, 'red.decision', 1, {
          reason: `Recon screen active: ${u.callsign} continuing reconnaissance along ${this.plRed.name}`,
          playId: 'red:screen', unitIds: [u.id],
        }, { significant: false });
      }
    }
  }

  /** Every posture, every 20 sim minutes: each log unit drives to resupply whichever alive main-body unit
   * currently has the lowest fuel-or-ammo, if it is not already on a resupply run. */
  private stepResupply(t: number, red: Unit[], orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    if (t % RESUPPLY_INTERVAL_TICKS !== 0) return;
    const logUnits = red.filter((u) => u.alive && u.type === 'log');
    if (logUnits.length === 0) return;
    const mainBody = red.filter((u) => u.alive && u.type !== 'log' && u.type !== 'hq' && u.type !== 'uas');
    if (mainBody.length === 0) return;

    for (const lu of logUnits) {
      const eligible = t >= (this.nextResupplyEligible.get(lu.id) ?? 0);
      const inFlight = orders.orders.some((o) => o.unitId === lu.id && o.playId === 'red:resupply' && !TERMINAL_STATUSES.has(o.status));
      if (!eligible || inFlight) continue;

      let target = mainBody[0];
      let bestScore = Math.min(target.fuel, target.ammo);
      for (const mu of mainBody) {
        const score = Math.min(mu.fuel, mu.ammo);
        if (score < bestScore) { target = mu; bestScore = score; }
      }
      if (bestScore >= 100) continue; // nobody needs it

      const order = orders.issue(t, 1, lu.id, 'resupply', { kind: 'unit', unitId: target.id }, units, grid, graphics, this.dials, 'red:resupply');
      this.nextResupplyEligible.set(lu.id, t + RESUPPLY_INTERVAL_TICKS);
      if (order.status !== 'rejected') {
        log.push(t, 'red.decision', 1, {
          reason: `Resupply: ${lu.callsign} moving to replenish ${target.callsign} (fuel ${Math.round(target.fuel)}%, ammo ${Math.round(target.ammo)}%)`,
          playId: 'red:resupply', unitIds: [lu.id],
        }, { significant: false });
      }
    }
  }

  /** forward only, one-shot: when blue's detected strength crosses PL RED in a third, commit the entire
   * combat force (armor/mech/inf/recon/spArty/mortar) to counterattack there - 'forward' no longer means
   * "hold PL RED and do nothing else after the initial defend". */
  private stepForward(t: number, red: Unit[], picture: Picture, orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    if (this.forwardCommitted || !this.plBlue) return;

    const sums: [number, number, number] = [0, 0, 0];
    for (const c of picture.contacts.values()) {
      if (c.reportedAt > t) continue;
      const depthVal = this.depthOf({ x: c.x, y: c.y });
      if (this.depthSign * (depthVal - this.plRedDepth) <= 0) continue; // not yet past PL RED
      sums[this.thirdOf(this.widthOf({ x: c.x, y: c.y }))] += 1;
    }

    let triggered: Third | -1 = -1;
    for (let i = 0; i < 3; i++) { if (sums[i] >= RESERVE_TRIGGER_PLATOONS) { triggered = i as Third; break; } }
    if (triggered === -1) return;

    const cell = chokepointCell(grid, this.plBlue, triggered, this.widthAxis, this.widthMin, this.widthMax)
      ?? (this.obj ? grid.cellOfCm(this.obj.pts[0].x, this.obj.pts[0].y) : null);
    if (cell === null) return;

    this.forwardCommitted = true; // one-shot regardless of outcome: red throws the counterattack once
    const force = red.filter((u) => u.alive && COUNTERATTACK_TYPES.has(u.type));
    const unitIds: number[] = [];
    for (const u of force) {
      const order = orders.issue(t, 1, u.id, 'attack', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:forward');
      if (order.status !== 'rejected') unitIds.push(u.id);
    }
    if (unitIds.length > 0) {
      log.push(t, 'red.decision', 1, {
        reason: `Forward defense committing the entire force: ${sums[triggered]} blue platoons crossed PL RED in the ${this.widthNames[triggered]} third`,
        playId: 'red:forward', unitIds,
      }, { significant: true });
    }
  }

  /** depth only: reserve (armor) counterattacks the third where >= 2 blue platoons are detected past PL WHITE. */
  private stepReserve(t: number, red: Unit[], picture: Picture, orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    const reserve = red.filter((u) => u.alive && u.type === 'armor' && !this.committedReserve.has(u.id));
    if (reserve.length === 0 || !this.plBlue) return;

    const sums: [number, number, number] = [0, 0, 0];
    for (const c of picture.contacts.values()) {
      if (c.reportedAt > t) continue; // not yet received by red's HQ
      const depthVal = this.depthOf({ x: c.x, y: c.y });
      if (this.depthSign * (depthVal - this.plWhiteDepth) <= 0) continue; // not yet past PL WHITE toward the objective
      sums[this.thirdOf(this.widthOf({ x: c.x, y: c.y }))] += 1;
    }

    let triggered: Third | -1 = -1;
    for (let i = 0; i < 3; i++) { if (sums[i] >= RESERVE_TRIGGER_PLATOONS) { triggered = i as Third; break; } }
    if (triggered === -1) return;

    const cell = chokepointCell(grid, this.plBlue, triggered, this.widthAxis, this.widthMin, this.widthMax);
    if (cell === null) return;

    const unitIds: number[] = [];
    for (const u of reserve) {
      const order = orders.issue(t, 1, u.id, 'attack', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:reserve');
      this.committedReserve.add(u.id);
      if (order.status !== 'rejected') unitIds.push(u.id);
    }
    if (unitIds.length > 0) {
      log.push(t, 'red.decision', 1, {
        reason: `Reserve committed: ${sums[triggered]} blue platoons detected crossing PL WHITE in the ${this.widthNames[triggered]} third`,
        playId: 'red:reserve', unitIds,
      }, { significant: false });
    }
  }

  /** depth only: when a main-body unit (mech/inf) drops below 50% strength, send one uncommitted reserve unit to defend at its cell. */
  private stepReinforce(t: number, red: Unit[], orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    const mainBody = red.filter(
      (u) => u.alive && (u.type === 'mech' || u.type === 'inf') && u.strength < REINFORCE_STRENGTH_THRESHOLD && !this.reinforcedFor.has(u.id),
    );
    if (mainBody.length === 0) return;

    for (const mu of mainBody) {
      const reserve = red.filter((u) => u.alive && u.type === 'armor' && !this.committedReserve.has(u.id));
      if (reserve.length === 0) break;
      const ru = reserve[0];
      const cell = grid.cellOfCm(mu.x, mu.y);
      const order = orders.issue(t, 1, ru.id, 'defend', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:reinforce');
      this.committedReserve.add(ru.id);
      this.reinforcedFor.add(mu.id);
      if (order.status !== 'rejected') {
        log.push(t, 'red.decision', 1, {
          reason: `Reinforcement sent: reserve unit ${ru.callsign} committed to reinforce ${mu.callsign}, below 50% strength`,
          playId: 'red:reinforce', unitIds: [ru.id],
        }, { significant: false });
      }
    }
  }

  /** screen only: engaged units under 70% strength fall back one PL (RED -> WHITE -> BLUE -> objective),
   * with an available artillery unit laying smoke on the withdrawing unit's position to cover it. */
  private stepDelay(t: number, red: Unit[], orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    for (const u of red) {
      if (!u.alive || !u.inContact || u.strength >= DELAY_STRENGTH_THRESHOLD) continue;

      const inFlight = orders.orders.some((o) => o.unitId === u.id && o.playId === 'red:delay' && !TERMINAL_STATUSES.has(o.status));
      if (inFlight) continue;

      const score = this.depthSign * this.depthOf({ x: u.x, y: u.y });
      const next = this.waypoints.find((w) => this.depthSign * w.depthVal > score) ?? this.waypoints[this.waypoints.length - 1];
      if (!next) continue;

      const third = this.thirdOf(this.widthOf({ x: u.x, y: u.y }));
      let cell: number | null = null;
      if (next.kind === 'pl') {
        cell = nearestCellOnPl(grid, next.graphic, third, this.widthAxis, this.widthMin, this.widthMax, { x: u.x, y: u.y });
      } else {
        const c = grid.cellOfCm(next.graphic.pts[0].x, next.graphic.pts[0].y);
        cell = grid.passable(c) ? c : null;
      }
      if (cell === null) continue;

      const order = orders.issue(t, 1, u.id, 'withdraw', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:delay');
      if (order.status !== 'rejected') {
        log.push(t, 'red.decision', 1, {
          reason: `${u.callsign} disengaging under pressure at ${Math.round(u.strength)}% strength, falling back to ${next.name}`,
          playId: 'red:delay', unitIds: [u.id],
        }, { significant: false });
        this.smokeWithdrawal(t, u, red, orders, units, grid, graphics, log);
      }
    }
  }

  /** Covers a withdrawing unit with smoke from the nearest available artillery unit, if any has ammo to spare. */
  private smokeWithdrawal(t: number, withdrawing: Unit, red: Unit[], orders: OrderBook, units: Unit[], grid: Grid, graphics: Graphic[], log: EventLog): void {
    const artUnits = red.filter((u) => u.alive && (u.type === 'spArty' || u.type === 'mortar') && u.ammo >= this.rules.fireMission.minAmmoToFire);
    if (artUnits.length === 0) return;
    let arty = artUnits[0];
    let bestD = grid.distCm(arty, withdrawing);
    for (const u of artUnits) {
      const d = grid.distCm(u, withdrawing);
      if (d < bestD) { arty = u; bestD = d; }
    }
    const cell = grid.cellOfCm(withdrawing.x, withdrawing.y);
    const order = orders.issue(t, 1, arty.id, 'smoke', { kind: 'point', cell }, units, grid, graphics, this.dials, 'red:smoke');
    if (order.status !== 'rejected') {
      log.push(t, 'red.decision', 1, {
        reason: `Smoke: ${arty.callsign} obscuring ${withdrawing.callsign}'s withdrawal`,
        playId: 'red:smoke', unitIds: [arty.id],
      }, { significant: false });
    }
  }
}
